import { Injectable } from '@angular/core';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import {
  HTTP_ERROR_TOAST_REQUEST,
  SUPPRESS_HTTP_ERROR_TOAST,
} from 'src/app/core/http-error-toast/http-error-toast.context';

const REFRESH_RETRY_ATTEMPTED = new HttpContextToken<boolean>(() => false);

@Injectable()
export class TokenRefreshInterceptor implements HttpInterceptor {
  constructor(private authService: AuthService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isProtectedApiRequest(req)) {
      return next.handle(req);
    }

    const accessToken = this.authService.getAccessToken();
    if (!accessToken) {
      return next.handle(req);
    }

    if (this.authService.isTokenExpired(accessToken)) {
      if (this.isRefreshTokenExpired()) {
        return this.endSession(req);
      }

      return this.refreshAndRetry(req, next);
    }

    return next.handle(this.withAccessToken(req, accessToken)).pipe(
      catchError((error: unknown) => {
        if (this.isUnauthorized(error) && !req.context.get(REFRESH_RETRY_ATTEMPTED)) {
          return this.refreshAndRetry(req, next);
        }

        return throwError(() => error);
      })
    );
  }

  private refreshAndRetry(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.isRefreshTokenExpired()) {
      return this.endSession(req);
    }

    return this.authService.refreshAccessToken().pipe(
      switchMap((accessToken) => {
        if (!accessToken) {
          return this.endSession(req);
        }

        return next.handle(this.withAccessToken(req, accessToken, true)).pipe(
          catchError((error: unknown) => this.isUnauthorized(error)
            ? this.endSession(req)
            : throwError(() => error))
        );
      }),
      catchError((error: unknown) => this.isUnauthorized(error)
        ? this.endSession(req)
        : throwError(() => error))
    );
  }

  private withAccessToken(
    req: HttpRequest<unknown>,
    accessToken: string,
    refreshRetryAttempted = false
  ): HttpRequest<unknown> {
    const authenticatedRequest = req.clone({
      context: refreshRetryAttempted
        ? req.context.set(REFRESH_RETRY_ATTEMPTED, true)
        : req.context,
      headers: req.headers.set('Authorization', `Bearer ${accessToken}`),
    });

    return authenticatedRequest.clone({
      context: authenticatedRequest.context.set(HTTP_ERROR_TOAST_REQUEST, authenticatedRequest),
    });
  }

  private endSession(req: HttpRequest<unknown>): Observable<never> {
    req.context.set(SUPPRESS_HTTP_ERROR_TOAST, true);
    this.authService.logout();

    return EMPTY;
  }

  private isRefreshTokenExpired(): boolean {
    const refreshToken = this.authService.getRefreshToken();
    return !refreshToken || this.authService.isTokenExpired(refreshToken);
  }

  private isProtectedApiRequest(req: HttpRequest<unknown>): boolean {
    return req.url.includes('/api/v1/')
      && !req.url.includes('/auth/login')
      && !req.url.includes('/auth/refresh');
  }

  private isUnauthorized(error: unknown): error is HttpErrorResponse {
    return error instanceof HttpErrorResponse && error.status === 401;
  }
}
